import { useEffect, useRef } from "react";
import { Plus, Minus, LocateFixed } from "lucide-react";
import { geoOrthographic, geoPath, geoGraticule10, geoDistance, geoInterpolate } from "d3-geo";
import { feature } from "topojson-client";
import world from "world-atlas/countries-110m.json";

const LAND = feature(world, world.objects.countries);

// Premium canvas Earth: real continents via d3-geo orthographic projection,
// atmospheric glow, 3D sheen, momentum drag + idle auto-spin.
export default function Globe({ countries, onSelect, focusCode, spinPaused, statusMap = {}, zoom = 1, visaMode = false, arcs = [], arcOrigin = null }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const pillRefs = useRef({});
  const rot = useRef([-20, -12]); // [lambda, phi]
  const vel = useRef([0.14, 0]);
  const target = useRef(null);
  const scaleMul = useRef(1);
  const scaleTarget = useRef(1);
  const dragging = useRef(false);
  const moved = useRef(false);
  const downPos = useRef({ x: 0, y: 0 });
  const markersRef = useRef([]);
  const planRef = useRef({ t: 0, set: new Set() });
  const pinching = useRef(false);
  const pinchStartDist = useRef(0);
  const pinchStartScale = useRef(1);
  const last = useRef({ x: 0, y: 0 });
  const raf = useRef(null);
  const visaRef = useRef(visaMode);
  const arcsRef = useRef(arcs);
  const arcOriginRef = useRef(arcOrigin);

  useEffect(() => { visaRef.current = visaMode; }, [visaMode]);
  useEffect(() => { arcsRef.current = arcs || []; }, [arcs]);
  useEffect(() => { arcOriginRef.current = arcOrigin; }, [arcOrigin]);

  useEffect(() => {
    if (zoom) scaleTarget.current = zoom;
  }, [zoom]);

  useEffect(() => {
    if (!focusCode) return;
    const c = countries.find((x) => x.code === focusCode);
    if (!c) return;
    target.current = [-c.lng, -c.lat];
    vel.current = [0, 0];
  }, [focusCode, countries]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let stars = null;
    let w = 0;
    let h = 0;
    const projection = geoOrthographic().clipAngle(90);
    const path = geoPath(projection, ctx);
    planRef.current = { t: 0, set: new Set() };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = wrapRef.current.clientWidth;
      h = wrapRef.current.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      stars = Array.from({ length: 120 }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * 1.3 + 0.2,
        a: Math.random() * 0.6 + 0.15,
      }));
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      const cx = w / 2;
      const cy = h / 2;
      const R = Math.min(w, h) * 0.44 * scaleMul.current;
      projection.scale(R).translate([cx, cy]).rotate([rot.current[0], rot.current[1]]);

      ctx.clearRect(0, 0, w, h);

      // starfield
      if (stars) {
        stars.forEach((s) => {
          ctx.beginPath();
          ctx.fillStyle = `rgba(226,232,240,${s.a})`;
          ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
          ctx.fill();
        });
      }

      // outer atmosphere halo
      const halo = ctx.createRadialGradient(cx, cy, R * 0.92, cx, cy, R * 1.4);
      halo.addColorStop(0, "rgba(56,189,248,0.28)");
      halo.addColorStop(0.5, "rgba(56,189,248,0.06)");
      halo.addColorStop(1, "rgba(56,189,248,0)");
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(cx, cy, R * 1.4, 0, Math.PI * 2);
      ctx.fill();

      // ocean sphere
      ctx.beginPath();
      path({ type: "Sphere" });
      const ocean = ctx.createRadialGradient(cx - R * 0.35, cy - R * 0.35, R * 0.1, cx, cy, R);
      ocean.addColorStop(0, "#0e2a52");
      ocean.addColorStop(0.6, "#0a1a34");
      ocean.addColorStop(1, "#060d1c");
      ctx.fillStyle = ocean;
      ctx.fill();

      // graticule
      ctx.beginPath();
      path(geoGraticule10());
      ctx.strokeStyle = "rgba(148,163,184,0.12)";
      ctx.lineWidth = 0.5;
      ctx.stroke();

      // continents
      ctx.beginPath();
      path(LAND);
      const landGrad = ctx.createRadialGradient(cx - R * 0.3, cy - R * 0.3, R * 0.1, cx, cy, R);
      landGrad.addColorStop(0, "#2f6b4f");
      landGrad.addColorStop(0.7, "#234d3c");
      landGrad.addColorStop(1, "#173328");
      ctx.fillStyle = landGrad;
      ctx.fill();
      ctx.strokeStyle = "rgba(226,194,117,0.22)";
      ctx.lineWidth = 0.5;
      ctx.stroke();

      // 3D sheen (top-left light)
      ctx.save();
      ctx.beginPath();
      path({ type: "Sphere" });
      ctx.clip();
      const sheen = ctx.createRadialGradient(cx - R * 0.45, cy - R * 0.5, R * 0.05, cx - R * 0.2, cy - R * 0.2, R * 1.1);
      sheen.addColorStop(0, "rgba(255,255,255,0.16)");
      sheen.addColorStop(0.4, "rgba(255,255,255,0.03)");
      sheen.addColorStop(1, "rgba(0,0,0,0.35)");
      ctx.fillStyle = sheen;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();

      // rim light
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(120,180,255,0.35)";
      ctx.lineWidth = 1.2;
      ctx.stroke();

      const center = [-rot.current[0], -rot.current[1]];

      // visa-free arcs
      if (visaRef.current && arcOriginRef.current) {
        const o = arcOriginRef.current; // [lng,lat]
        (arcsRef.current || []).forEach((dest) => {
          const interp = geoInterpolate(o, dest);
          ctx.beginPath();
          let started = false;
          for (let i = 0; i <= 40; i++) {
            const p = projection(interp(i / 40));
            if (!p) { started = false; continue; }
            if (!started) { ctx.moveTo(p[0], p[1]); started = true; } else ctx.lineTo(p[0], p[1]);
          }
          ctx.strokeStyle = "rgba(56,189,248,0.35)";
          ctx.lineWidth = 0.7;
          ctx.stroke();
          const dp = projection(dest);
          if (dp && geoDistance(dest, center) < Math.PI / 2) {
            ctx.beginPath();
            ctx.arc(dp[0], dp[1], 1.7, 0, Math.PI * 2);
            ctx.fillStyle = "#7dd3fc";
            ctx.fill();
          }
        });
        const op = projection(o);
        if (op && geoDistance(o, center) < Math.PI / 2) {
          const pr = 5 + Math.sin(Date.now() / 400) * 1.5;
          ctx.beginPath();
          ctx.arc(op[0], op[1], pr + 6, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(226,194,117,0.25)";
          ctx.fill();
          ctx.beginPath();
          ctx.arc(op[0], op[1], 5, 0, Math.PI * 2);
          ctx.fillStyle = "#E2C275";
          ctx.fill();
          ctx.strokeStyle = "#fff";
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }
      }

      // markers + pills
      const now = Date.now() / 600;
      const frame = [];
      const candidates = [];
      countries.forEach((c) => {
        const d = geoDistance([c.lng, c.lat], center);
        const visible = d < Math.PI / 2 - 0.03;
        const pt = projection([c.lng, c.lat]);
        frame.push({ code: c.code, x: pt ? pt[0] : 0, y: pt ? pt[1] : 0, visible: visible && !!pt });
        const status = statusMap[c.code];
        const color = status === "active" ? "#10B981" : status === "saved" ? "#E2C275" : c.accent || "#E2C275";
        if (visible && pt) {
          const pr = 3 + Math.sin(now + c.lat) * 1.3;
          ctx.beginPath();
          ctx.arc(pt[0], pt[1], pr + 7, 0, Math.PI * 2);
          ctx.fillStyle = color + "33";
          ctx.fill();
          ctx.beginPath();
          ctx.arc(pt[0], pt[1], 3.8, 0, Math.PI * 2);
          ctx.fillStyle = color;
          ctx.fill();
          ctx.strokeStyle = "rgba(255,255,255,0.9)";
          ctx.lineWidth = 1;
          ctx.stroke();
        }
        const el = pillRefs.current[c.code];
        if (el) {
          const facing = !spinPaused && visible && !!pt && d < Math.PI / 2 - 0.2;
          const w = 40 + (c.code.length + (c.from_display ? c.from_display.length : 6)) * 6.4;
          candidates.push({ code: c.code, el, x: pt ? pt[0] : 0, y: pt ? pt[1] : 0, d, facing, w });
        }
      });
      markersRef.current = frame;

      // Intelligent de-clutter, recomputed on a throttle so labels stay stable.
      // (Recomputing selection every frame made borderline pills flicker/"vibrate".)
      const PILL_CAP = 6;
      const nowMs = Date.now();
      if (nowMs - planRef.current.t > 600 || planRef.current.set.size === 0) {
        const facing = candidates.filter((c) => c.facing).sort((a, b) => a.d - b.d);
        const placed = [];
        const set = new Set();
        facing.forEach((cand) => {
          const box = { l: cand.x - cand.w / 2, r: cand.x + cand.w / 2, t: cand.y - 24, b: cand.y - 2 };
          const collides = placed.some((p) => !(box.r < p.l || box.l > p.r || box.b < p.t || box.t > p.b));
          if (placed.length < PILL_CAP && !collides) {
            placed.push(box);
            set.add(cand.code);
          }
        });
        planRef.current = { t: nowMs, set };
      }
      const plan = visaRef.current ? new Set() : planRef.current.set;
      candidates.forEach((cand) => {
        if (!spinPaused && cand.facing && plan.has(cand.code)) {
          cand.el.style.display = "flex";
          cand.el.style.left = Math.round(cand.x) + "px";
          cand.el.style.top = Math.round(cand.y - 12) + "px";
          cand.el.style.opacity = "1";
        } else {
          cand.el.style.display = "none";
        }
      });



      // physics
      if (target.current) {
        rot.current[0] += (target.current[0] - rot.current[0]) * 0.1;
        rot.current[1] += (target.current[1] - rot.current[1]) * 0.1;
        if (
          Math.abs(target.current[0] - rot.current[0]) < 0.1 &&
          Math.abs(target.current[1] - rot.current[1]) < 0.1
        ) {
          rot.current = [...target.current];
          target.current = null;
        }
      } else if (!dragging.current) {
        rot.current[0] += vel.current[0];
        rot.current[1] += vel.current[1];
        vel.current[0] *= 0.95;
        vel.current[1] *= 0.95;
        if (!spinPaused && Math.abs(vel.current[0]) < 0.05) {
          rot.current[0] += 0.12; // idle auto-spin
        }
      }
      rot.current[1] = Math.max(-88, Math.min(88, rot.current[1]));
      scaleMul.current += (scaleTarget.current - scaleMul.current) * 0.12;

      raf.current = requestAnimationFrame(draw);
    };
    raf.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf.current);
      window.removeEventListener("resize", resize);
    };
  }, [countries, spinPaused, statusMap]);

  const touchDist = (touches) =>
    Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);

  const onDown = (e) => {
    if (e.touches && e.touches.length === 2) {
      pinching.current = true;
      dragging.current = false;
      target.current = null;
      pinchStartDist.current = touchDist(e.touches);
      pinchStartScale.current = scaleTarget.current;
      return;
    }
    dragging.current = true;
    moved.current = false;
    target.current = null;
    const p = e.touches ? e.touches[0] : e;
    last.current = { x: p.clientX, y: p.clientY };
    downPos.current = { x: p.clientX, y: p.clientY };
    if (e.currentTarget.setPointerCapture && e.pointerId != null)
      e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e) => {
    if (pinching.current && e.touches && e.touches.length === 2) {
      const d = touchDist(e.touches);
      if (pinchStartDist.current > 0) {
        const val = Math.max(0.75, Math.min(3.5, (pinchStartScale.current * d) / pinchStartDist.current));
        scaleTarget.current = val;
        scaleMul.current = val; // immediate response while pinching
      }
      return;
    }
    if (!dragging.current) return;
    const p = e.touches ? e.touches[0] : e;
    const dx = p.clientX - last.current.x;
    const dy = p.clientY - last.current.y;
    if (Math.hypot(p.clientX - downPos.current.x, p.clientY - downPos.current.y) > 6) moved.current = true;
    last.current = { x: p.clientX, y: p.clientY };
    const k = 0.28;
    rot.current[0] += dx * k;
    rot.current[1] -= dy * k;
    vel.current = [dx * k, -dy * k];
  };
  const onUp = (e) => {
    if (pinching.current) {
      const remaining = e.touches ? e.touches.length : 0;
      if (remaining < 2) {
        pinching.current = false;
        if (e.touches && e.touches.length === 1) {
          // one finger left — re-anchor drag so the view doesn't jump
          dragging.current = true;
          moved.current = true;
          const p = e.touches[0];
          last.current = { x: p.clientX, y: p.clientY };
          downPos.current = { x: p.clientX, y: p.clientY };
        } else {
          dragging.current = false;
        }
      }
      return;
    }
    dragging.current = false;
    if (moved.current) return;
    const p = e.changedTouches ? e.changedTouches[0] : e;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = p.clientX - rect.left;
    const y = p.clientY - rect.top;
    let best = null;
    let bestD = 22; // px hit radius
    markersRef.current.forEach((m) => {
      if (!m.visible) return;
      const d = Math.hypot(m.x - x, m.y - y);
      if (d < bestD) {
        bestD = d;
        best = m.code;
      }
    });
    if (best) {
      const c = countries.find((k) => k.code === best);
      if (c) onSelect(c);
    }
  };

  const zoomBtn = (f) => {
    scaleTarget.current = Math.max(0.75, Math.min(3.5, scaleTarget.current * f));
  };
  const reset = () => {
    scaleTarget.current = 1;
    target.current = [-20, -12];
  };

  return (
    <div ref={wrapRef} className="relative h-full w-full select-none" style={{ touchAction: "pan-y" }}>
      <canvas
        ref={canvasRef}
        data-testid="interactive-globe-canvas"
        className="h-full w-full cursor-grab active:cursor-grabbing"
        style={{ touchAction: "none" }}
        onMouseDown={onDown}
        onMouseMove={onMove}
        onMouseUp={onUp}
        onMouseLeave={onUp}
        onTouchStart={onDown}
        onTouchMove={onMove}
        onTouchEnd={onUp}
      />

      {countries.map((c) => (
        <button
          key={c.code}
          ref={(el) => (pillRefs.current[c.code] = el)}
          data-testid={`opportunity-pill-${c.code}`}
          onClick={() => onSelect(c)}
          className="absolute z-10 -translate-x-1/2 -translate-y-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium backdrop-blur-md transition-transform hover:scale-105"
          style={{
            display: "none",
            background: "rgba(10,14,23,0.85)",
            borderColor: "rgba(226,194,117,0.45)",
            color: "#fff",
            whiteSpace: "nowrap",
          }}
        >
          <span>{c.flag}</span>
          <span className="font-semibold">{c.code}</span>
          <span style={{ color: "#E2C275" }} className="font-mono text-[10px]">
            {c.from_display}
          </span>
        </button>
      ))}

      <div className="absolute bottom-3 right-3 z-10 flex flex-col gap-2">
        <GlobeBtn testid="globe-control-zoom-in" onClick={() => zoomBtn(1.18)}>
          <Plus size={16} />
        </GlobeBtn>
        <GlobeBtn testid="globe-control-zoom-out" onClick={() => zoomBtn(0.85)}>
          <Minus size={16} />
        </GlobeBtn>
        <GlobeBtn testid="globe-control-reset" onClick={reset}>
          <LocateFixed size={16} />
        </GlobeBtn>
      </div>
    </div>
  );
}

function GlobeBtn({ children, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/5 text-slate-200 backdrop-blur-md transition-colors hover:border-amber-300/40 hover:text-amber-200"
    >
      {children}
    </button>
  );
}
